/**
 * webPush — browser Push API subscription helpers (web/desktop only).
 *
 * Native iOS/Android use APNs/FCM via expo-notifications and never touch this
 * file. On web/electron we drive a separate flow:
 *
 *   1. Service worker (`mobile/web/service-worker.js`) registered at app start
 *      from `index.html`. It handles `push`, `notificationclick`, and
 *      `pushsubscriptionchange` events, and forwards lifecycle/click data to
 *      the React tree via `postMessage` (DesktopLayout listens).
 *   2. A user action calls `subscribeWebPush()`: it may request permission,
 *      create a `PushSubscription`, records durable opt-in for this user on
 *      this installation, and registers the endpoint as user-initiated.
 *   3. Startup and subscription rotation call `reconcileWebPush()`: it never
 *      prompts. It can register an existing legacy subscription, or recreate
 *      a missing subscription only when this user previously opted in on this
 *      installation. Maintenance registrations are explicitly silent.
 *   4. `unsubscribeWebPush()` tears down the browser subscription and marks
 *      the backend endpoint inactive. Settings records a durable opt-out;
 *      sign-out can preserve the prior preference while unbinding the device.
 *
 * All entry points are no-ops outside web/electron and degrade gracefully
 * when the browser doesn't expose `serviceWorker` / `PushManager`.
 */

import config from '../config';
import { apiPost } from '../utilities/apiClient';
import { isWeb } from '../utilities/platform';
import { store } from '../store';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PushSubscriptionConfig {
  // JSON-stringified before send (column on push_subscriptions is JSONB
  // and the backend lambda spreads ...config into a raw insert that
  // doesn't auto-stringify). Type is `string` on the wire, the DB stores
  // it as the parsed JSON object.
  device?: string;
  device_id?: string;
  time_zone?: string;
  lifecycle_generation?: number;
}

interface SubscribeResult {
  success: boolean;
  message?: string;
  /** The push provider rejected the endpoint as expired. */
  expired?: boolean;
  /** True when the browser/runtime can't host a push subscription (no SW, etc.) */
  unsupported?: boolean;
  /** True when permission was actively denied by the user. */
  denied?: boolean;
  /**
   * True when nothing changed — both the browser and backend already had this
   * endpoint on file before the call. Lets the UI distinguish "newly enabled"
   * from "already enabled" so a toast/alert can reflect reality instead of
   * implying a fresh subscription was created.
   */
  alreadyActive?: boolean;
  /** True when reconciliation intentionally made no change. */
  skipped?: boolean;
}

interface UnsubscribeOptions {
  /** Defaults to an explicit settings opt-out. */
  reason?: 'sign_out' | 'user_disabled';
}

export type CurrentUserWebPushStatus = 'enabled' | 'disabled' | 'unknown';

// ── Helpers ───────────────────────────────────────────────────────────────────

const isWebRuntime = (): boolean =>
  isWeb && typeof navigator !== 'undefined' && typeof window !== 'undefined';

const hasPushSupport = (): boolean =>
  isWebRuntime()
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && typeof Notification !== 'undefined';

const WEB_PUSH_DEVICE_ID_KEY = 'eleplan.webPush.deviceId';
const WEB_PUSH_PREFERENCE_PREFIX = 'eleplan.webPush.preference';
const WEB_PUSH_LIFECYCLE_PREFIX = 'eleplan.webPush.lifecycle';

type WebPushPreference = 'enabled' | 'disabled';
type WebPushLifecycleReason = 'sign_out' | 'user_disabled';
type WebPushLifecycle = {
  generation: number;
  state: 'active' | 'inactive';
  reason?: WebPushLifecycleReason;
};

let memoryDeviceId = '';
let memoryLifecycle: WebPushLifecycle | null = null;
const memoryPreferences = new Map<string, WebPushPreference>();

// PushManager and backend writes must not race. In particular, two UI surfaces
// can request enable at the same time while App startup is also reconciling.
// A tiny promise queue preserves each caller's intent while ensuring only one
// subscription mutation runs at a time.
let operationTail: Promise<void> = Promise.resolve();

function serializeOperation<T>(operation: () => Promise<T>): Promise<T> {
  const run = operationTail.then(operation, operation);
  operationTail = run.then(() => undefined, () => undefined);
  return run;
}

/**
 * Convert a base64url-encoded VAPID key to the Uint8Array format the Push API
 * expects for `applicationServerKey`. Mirrors the legacy web app's helper.
 */
function vapidKeyToUint8Array(base64Url: string): Uint8Array | null {
  try {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.error('[webPush] failed to decode VAPID key', err);
    }
    return null;
  }
}

/**
 * Wait for the registered service worker to reach the `activated` state.
 * `navigator.serviceWorker.ready` resolves when there's an active SW for the
 * scope.
 *
 * The timeout here is ONLY a backstop for a genuinely stuck registration — it
 * must NOT clip a normal cold activation. The previous 2s cap did exactly that:
 * on a cold load the SW often isn't activated within 2s, so getSubscription()
 * returned null and the settings toggle false-negatived to OFF even though the
 * browser was subscribed. That single unreliable read is what every prior
 * "source of truth" patch was really fighting. We fast-fail when no SW is
 * registered at all (so dev/Expo runs don't hang), which means whenever a
 * registration DOES exist, `ready` will resolve once it activates — a generous
 * backstop just guards against a wedged install.
 *
 * Returns `null` when no SW is registered for this origin (Expo's
 * `expo start --web` dev server doesn't serve our SW; only the Electron/webpack
 * build at `desktop/webpack.config.js` and `mobile/web/index.html` does), or if
 * an existing registration never activates within the backstop window.
 */
async function awaitActiveServiceWorker(timeoutMs = 10000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  try {
    // Fast-fail when nothing is registered — `serviceWorker.ready` would
    // hang forever (it never rejects on its own) if no registration exists,
    // so we'd otherwise eat the full timeout on every dev-server load.
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) return null;
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration>((_, reject) => {
        timeout = setTimeout(() => reject(new Error('service-worker-timeout')), timeoutMs);
      }),
    ]);
  } catch {
    return null;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

/**
 * True when the page has no service worker registration at all. Used to
 * distinguish "push genuinely unavailable on this surface" from "push
 * temporarily failed" so the bootstrap caller doesn't yell about Expo
 * dev-server runs that never register a SW in the first place.
 */
async function hasAnyServiceWorkerRegistration(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    return regs.length > 0;
  } catch {
    return false;
  }
}

/** Detect a small descriptor for the device — best-effort, never throws. */
function describeDevice(): Record<string, unknown> {
  if (!isWebRuntime()) return {};
  const ua = navigator.userAgent || '';
  const platform = (navigator as any).userAgentData?.platform || (navigator as any).platform || '';
  const language = navigator.language || '';
  return { ua, platform, language, deviceType: 'web' };
}

function readTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}

/**
 * Persist a per-install device id under localStorage so the backend can
 * distinguish multiple devices for the same Cognito user. The mobile redux
 * store doesn't model `device_id` yet; using localStorage keeps this isolated
 * and doesn't drag the legacy web's deviceId reducer over.
 */
function getOrCreateDeviceId(): string {
  if (!isWebRuntime()) return '';
  const fallback = () => {
    if (!memoryDeviceId) {
      memoryDeviceId = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
        ? String((crypto as any).randomUUID())
        : 'web-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
    return memoryDeviceId;
  };
  if (typeof localStorage === 'undefined') return fallback();
  try {
    let id = localStorage.getItem(WEB_PUSH_DEVICE_ID_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
        ? String((crypto as any).randomUUID())
        : 'web-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(WEB_PUSH_DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return fallback();
  }
}

function lifecycleStorageKey(): string {
  return `${WEB_PUSH_LIFECYCLE_PREFIX}.${getOrCreateDeviceId()}`;
}

function readLifecycle(): WebPushLifecycle {
  if (isWebRuntime() && typeof localStorage !== 'undefined') {
    try {
      const parsed = JSON.parse(localStorage.getItem(lifecycleStorageKey()) || 'null');
      if (
        parsed
        && Number.isSafeInteger(parsed.generation)
        && parsed.generation >= 0
        && (parsed.state === 'active' || parsed.state === 'inactive')
      ) {
        memoryLifecycle = parsed;
        return parsed;
      }
    } catch {
      // Fall through to the in-memory state for hardened/private contexts.
    }
  }
  return memoryLifecycle || { generation: 0, state: 'active' };
}

function writeLifecycle(lifecycle: WebPushLifecycle): WebPushLifecycle {
  memoryLifecycle = lifecycle;
  if (isWebRuntime() && typeof localStorage !== 'undefined') {
    try { localStorage.setItem(lifecycleStorageKey(), JSON.stringify(lifecycle)); } catch { /* best-effort */ }
  }
  return lifecycle;
}

function transitionLifecycle(
  state: WebPushLifecycle['state'],
  reason?: WebPushLifecycleReason,
): WebPushLifecycle {
  const current = readLifecycle();
  return writeLifecycle({
    generation: Math.max(current.generation + 1, Date.now()),
    state,
    ...(state === 'inactive' && reason ? { reason } : {}),
  });
}

function lifecycleMatches(expected: WebPushLifecycle): boolean {
  const current = readLifecycle();
  return current.generation === expected.generation && current.state === expected.state;
}

async function stopSupersededRegistration(
  expected: WebPushLifecycle,
  subscription?: PushSubscription | null,
): Promise<'current' | 'superseded' | 'cleanup_failed'> {
  const current = readLifecycle();
  if (current.generation === expected.generation && current.state === expected.state) return 'current';
  // If the newer intent is disable/sign-out, invalidate a subscription this
  // stale enable may just have created. If the newer intent is active, leave
  // origin state alone for the new authenticated session to own/reconcile.
  if (current.state === 'inactive' && subscription) {
    try {
      if ((await subscription.unsubscribe()) === false) return 'cleanup_failed';
    } catch {
      return 'cleanup_failed';
    }
  }
  return 'superseded';
}

function supersededRegistrationResult(
  state: 'current' | 'superseded' | 'cleanup_failed',
): SubscribeResult | null {
  if (state === 'current') return null;
  if (state === 'cleanup_failed') {
    return {
      success: false,
      message: 'A newer notification setting took effect, but browser cleanup is still pending.',
    };
  }
  return { success: true, skipped: true };
}

/** Mark a newly authenticated browser session as newer than prior sign-out work. */
export function markWebPushSessionActive(): void {
  transitionLifecycle('active');
}

/**
 * Pull `account_id` and `cognito_id` out of the redux store. Returns null when
 * the user isn't authenticated yet — caller should treat this as "skip for now,
 * try again next time auth state changes."
 */
function getAuthIdentity(): { account_id: string; cognito_id: string } | null {
  const state: any = store.getState();
  const account_id = state?.session?.account?.account_id;
  // ED-2334. The authenticated user's cognito_id, not the account
  // owner's. For partner-invited members, `session.account.cognito_id`
  // is the partner OWNER's id (the account belongs to the owner), so
  // the previous precedence sent the owner's id in both URL segments.
  // The push-subscription endpoint requires `cognito_id` to match the
  // JWT sub or it returns 401. The user's own cognito_id is what the
  // auth flow set on `authentication.user.cognito_id` / `session.user.cognito_id`,
  // so prefer those.
  const cognito_id =
    state?.authentication?.user?.cognito_id ||
    state?.session?.user?.cognito_id ||
    state?.session?.account?.cognito_id;
  if (!account_id || !cognito_id) return null;
  return { account_id, cognito_id };
}

function preferenceStorageKey(cognitoId: string): string {
  return `${WEB_PUSH_PREFERENCE_PREFIX}.${encodeURIComponent(cognitoId)}.${getOrCreateDeviceId()}`;
}

function readPreference(cognitoId: string): WebPushPreference | null {
  const key = preferenceStorageKey(cognitoId);
  if (!isWebRuntime() || typeof localStorage === 'undefined') {
    return memoryPreferences.get(key) ?? null;
  }
  try {
    const value = localStorage.getItem(key);
    if (value === 'enabled' || value === 'disabled') {
      memoryPreferences.set(key, value);
      return value;
    }
    return memoryPreferences.get(key) ?? null;
  } catch {
    return memoryPreferences.get(key) ?? null;
  }
}

function writePreference(cognitoId: string, preference: WebPushPreference): void {
  const key = preferenceStorageKey(cognitoId);
  memoryPreferences.set(key, preference);
  if (!isWebRuntime() || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, preference);
  } catch {
    // Storage may be unavailable in hardened/private browser contexts. The
    // browser subscription remains authoritative for the current session.
  }
}

/**
 * Returns true when redux's user record already lists an active
 * push_subscriptions row whose endpoint matches the SW subscription we're
 * about to register. `getAccountUser`
 * (backend/api/client/utilities/account-utils.js:188-202) joins
 * push_subscriptions into the /users/get-user payload — `endpoint` is
 * pulled out of the JSONB subscription column. When the user record
 * hasn't loaded yet (`user === false` on initial mount), we return false
 * so the POST runs and the backend's "already exists" branch handles
 * dedup.
 */
function backendAlreadyHasEndpoint(endpoint: string): boolean {
  const state: any = store.getState();
  const subscriptions = state?.authentication?.user?.push_subscriptions;
  if (!Array.isArray(subscriptions)) return false;
  return subscriptions.some(
    (row: any) => row?.status === 'active' && row?.endpoint === endpoint,
  );
}

function backendAlreadyHasInstallation(deviceId: string): boolean {
  if (!deviceId) return false;
  const state: any = store.getState();
  const subscriptions = state?.authentication?.user?.push_subscriptions;
  if (!Array.isArray(subscriptions)) return false;
  return subscriptions.some(
    (row: any) => row?.status === 'active' && row?.device_id === deviceId,
  );
}

/**
 * Whether Redux has finished loading this user's backend push rows. An absent
 * property means "unknown yet"; an explicit []/null means "loaded, none".
 * That distinction prevents startup from deleting a valid legacy endpoint
 * merely because the user payload has not arrived yet.
 */
function hasLoadedBackendPushState(): boolean {
  const state: any = store.getState();
  const user = state?.authentication?.user;
  return !!user
    && typeof user === 'object'
    && Object.prototype.hasOwnProperty.call(user, 'push_subscriptions');
}

async function deactivateBackendPush(
  auth: { account_id: string; cognito_id: string } | null,
  {
    endpoint,
    deviceId,
    reason,
    lifecycleGeneration,
    subscription,
  }: {
    endpoint?: string | null;
    deviceId: string;
    reason: 'sign_out' | 'user_disabled';
    lifecycleGeneration: number;
    subscription?: PushSubscription | null;
  },
): Promise<boolean> {
  if (!auth || (!endpoint && !deviceId)) return false;
  try {
    const result = await apiPost(
      `/users/update-push-subscription/${auth.account_id}/${auth.cognito_id}`,
      {
        ...(endpoint ? { endpoint } : {}),
        device_id: deviceId || null,
        lifecycle_generation: lifecycleGeneration,
        ...(subscription
          ? {
            subscription: typeof subscription.toJSON === 'function'
              ? subscription.toJSON()
              : subscription,
          }
          : {}),
        updates: { status: 'inactive' },
        reason,
      },
    );
    return result?.success === true;
  } catch {
    return false;
  }
}

async function cleanupCurrentBrowserSubscription(
  expectedLifecycle: WebPushLifecycle,
): Promise<{
  cleaned: boolean;
  superseded?: boolean;
  message?: string;
  subscription?: PushSubscription | null;
}> {
  if (!hasPushSupport()) return { cleaned: true };
  let subscription: PushSubscription | null = null;
  try {
    if (!(await hasAnyServiceWorkerRegistration())) return { cleaned: true };
    const reg = await awaitActiveServiceWorker();
    if (!reg) return { cleaned: false, message: 'Service worker not ready.' };
    subscription = await reg.pushManager.getSubscription();
    if (!lifecycleMatches(expectedLifecycle)) return { cleaned: false, superseded: true };
    if (!subscription) return { cleaned: true };
    const cleaned = (await subscription.unsubscribe()) !== false;
    if (!lifecycleMatches(expectedLifecycle)) return { cleaned: false, superseded: true };
    return cleaned
      ? { cleaned: true }
      : { cleaned: false, message: 'Push unsubscribe was not confirmed.', subscription };
  } catch (err: any) {
    return { cleaned: false, message: err?.message || 'Push unsubscribe failed.', subscription };
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns the existing browser push subscription, or null. Safe to call in any
 * environment — returns null on native and on browsers without push support.
 */
export async function getCurrentWebPushSubscription(): Promise<PushSubscription | null> {
  if (!hasPushSupport()) return null;
  try {
    const reg = await awaitActiveServiceWorker();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Whether browser push is usable by the currently authenticated user.
 *
 * A PushManager subscription belongs to the browser origin, not to a signed-in
 * Eleplan user. Treating its presence as "enabled" can therefore expose the
 * previous user's state on a shared browser. Current-user ownership comes from
 * either this user's durable per-installation opt-in, or (for legacy users
 * created before that preference existed) an active backend row for the exact
 * endpoint. When the user rows have not loaded yet, return `unknown` rather
 * than adopting or rejecting an origin-level subscription on a guess.
 */
export async function getCurrentUserWebPushStatus(): Promise<CurrentUserWebPushStatus> {
  if (!hasPushSupport()) return 'disabled';

  const auth = getAuthIdentity();
  if (!auth) return 'unknown';
  const lifecycle = readLifecycle();
  if (lifecycle.state === 'inactive') return 'disabled';

  const preference = readPreference(auth.cognito_id);
  if (preference === 'disabled') return 'disabled';

  const subscription = await getCurrentWebPushSubscription();

  // The identity may have changed while the service worker was becoming
  // active. Never apply the previous user's ownership result to the new one.
  if (getAuthIdentity()?.cognito_id !== auth.cognito_id) return 'unknown';
  if (!lifecycleMatches(lifecycle)) return 'disabled';

  if (!subscription || Notification.permission !== 'granted') return 'disabled';
  if (preference === 'enabled') return 'enabled';
  if (!hasLoadedBackendPushState()) return 'unknown';

  return backendAlreadyHasEndpoint(subscription.endpoint) ? 'enabled' : 'disabled';
}

/**
 * Invalidate the browser endpoint without making an authenticated API call.
 * Terminal token expiry uses this before identity state is cleared; keeping it
 * outside the serialized API-backed queue avoids a refresh -> cleanup ->
 * refresh deadlock when expiry is discovered during push registration itself.
 */
export async function unsubscribeBrowserPushOnly(): Promise<boolean> {
  const lifecycle = transitionLifecycle('inactive', 'sign_out');
  const result = await cleanupCurrentBrowserSubscription(lifecycle);
  return result.cleaned;
}

interface RegistrationOptions {
  userInitiated: boolean;
  retryExpired?: boolean;
  expectedLifecycle?: WebPushLifecycle;
}

async function registerWebPush({
  userInitiated,
  retryExpired = true,
  expectedLifecycle,
}: RegistrationOptions): Promise<SubscribeResult> {
  if (!hasPushSupport()) return { success: false, unsupported: true, message: 'Push not supported in this runtime.' };

  const auth = getAuthIdentity();
  if (!auth) return { success: false, message: 'Not authenticated.' };

  const deviceId = getOrCreateDeviceId();
  let lifecycle = expectedLifecycle || readLifecycle();
  if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
  const preference = readPreference(auth.cognito_id);
  const knownBackendInstallation = backendAlreadyHasInstallation(deviceId);
  if (!userInitiated && lifecycle.state === 'inactive') {
    const browserCleanup = await cleanupCurrentBrowserSubscription(lifecycle);
    if (browserCleanup.superseded || !lifecycleMatches(lifecycle)) {
      return { success: true, skipped: true };
    }
    const backendCleaned = await deactivateBackendPush(auth, {
      deviceId,
      reason: lifecycle.reason || 'sign_out',
      lifecycleGeneration: lifecycle.generation,
      subscription: browserCleanup.subscription,
    });
    // A null preference plus inactive lifecycle is the durable retry marker
    // for a prior user's orphan endpoint. Deactivating the current user's rows
    // cannot make that foreign endpoint safe; browser cleanup must succeed.
    if (!browserCleanup.cleaned && preference === null) {
      return {
        success: false,
        message: browserCleanup.message || 'Could not disconnect the previous browser notification endpoint.',
      };
    }
    if (browserCleanup.cleaned || backendCleaned) return { success: true, skipped: true };
    return {
      success: false,
      message: browserCleanup.message || 'Could not finish notification cleanup.',
    };
  }
  // A durable opt-out always wins, including when the browser still exposes a
  // stale subscription after an earlier unsubscribe failure. Explicit user
  // enable is the only path allowed to reverse it.
  if (!userInitiated && preference === 'disabled') {
    // Repair any prior browser-cleanup/network failure without recreating or
    // prompting. A PushManager subscription belongs to the origin, so it may
    // still be a prior user's endpoint on a shared browser; disabling B must
    // remove that endpoint before B's session can be considered safe.
    lifecycle = transitionLifecycle('inactive', 'user_disabled');
    const browserCleanup = await cleanupCurrentBrowserSubscription(lifecycle);
    if (browserCleanup.superseded || !lifecycleMatches(lifecycle)) {
      return { success: true, skipped: true };
    }
    const backendCleaned = await deactivateBackendPush(auth, {
      deviceId,
      reason: 'user_disabled',
      lifecycleGeneration: lifecycle.generation,
      subscription: browserCleanup.subscription,
    });
    if (!browserCleanup.cleaned) {
      return {
        success: false,
        message: browserCleanup.message || 'Could not disconnect browser notifications.',
      };
    }
    if (!backendCleaned) {
      return {
        success: false,
        message: 'Browser notifications were removed, but server cleanup is still pending.',
      };
    }
    return { success: true, skipped: true };
  }

  // Distinguish "no SW exists on this surface" (Expo web dev server, certain
  // CDN/preview hosts that don't ship the SW asset) from "SW exists but isn't
  // active yet" (slow install, sw.js fetch failed). The former is a genuine
  // unsupported runtime; the latter is a real failure worth logging.
  if (!(await hasAnyServiceWorkerRegistration())) {
    return { success: false, unsupported: true, message: 'No service worker registered for this origin.' };
  }

  const reg = await awaitActiveServiceWorker();
  if (!reg) return { success: false, message: 'Service worker not ready.' };
  if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };

  // Permission prompts are user-action only. Startup and service-worker
  // maintenance never surprise the user with an OS/browser permission sheet.
  if (Notification.permission === 'denied') {
    return userInitiated
      ? { success: false, denied: true, message: 'Notification permission denied.' }
      : { success: true, denied: true, skipped: true };
  }
  if (Notification.permission !== 'granted') {
    if (!userInitiated) return { success: true, skipped: true };
    let result: NotificationPermission;
    try {
      result = await Notification.requestPermission();
    } catch {
      return { success: false, message: 'Permission request failed.' };
    }
    if (result !== 'granted') return { success: false, denied: true, message: 'Notification permission not granted.' };
    if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
  }

  let subscription: PushSubscription | null = null;
  let preexistingBrowserSub = false;
  try {
    subscription = await reg.pushManager.getSubscription();
    const existingSuperseded = supersededRegistrationResult(
      await stopSupersededRegistration(lifecycle, subscription),
    );
    if (existingSuperseded) return existingSuperseded;
    preexistingBrowserSub = !!subscription;
    if (!subscription) {
      // Missing subscriptions may be created by a direct user action, or by
      // silent maintenance only when this same user explicitly opted in on
      // this installation before (for example, after sign-out unbound it).
      if (
        !userInitiated
        && preference !== 'enabled'
        && !(preference === null && knownBackendInstallation)
      ) {
        return { success: true, skipped: true };
      }
      if (preference === null && knownBackendInstallation) {
        writePreference(auth.cognito_id, 'enabled');
      }
      if (!config.webPush.publicKey) {
        return { success: false, message: 'VAPID public key missing — set EXPO_PUBLIC_VAPID_PUBLIC_KEY.' };
      }
      const applicationServerKey = vapidKeyToUint8Array(config.webPush.publicKey);
      if (!applicationServerKey) {
        return { success: false, message: 'Invalid VAPID public key.' };
      }
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });
      const createdSuperseded = supersededRegistrationResult(
        await stopSupersededRegistration(lifecycle, subscription),
      );
      if (createdSuperseded) return createdSuperseded;
    }
  } catch (err: any) {
    return { success: false, message: err?.message || 'Push subscribe failed.' };
  }

  const knownBackendEndpoint = backendAlreadyHasEndpoint(subscription.endpoint);

  // Consent is established once the user's explicit action produces or adopts
  // a browser subscription. Persist that intent before the network write so a
  // temporary API failure can be repaired silently on a later startup.
  if (userInitiated) {
    if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
    writePreference(auth.cognito_id, 'enabled');
  }

  // An orphan browser subscription is not proof that this authenticated user
  // consented. On a shared installation it may belong to the person who just
  // signed out. Migrate legacy consent only when the current user's own Redux
  // payload already lists this exact active endpoint; otherwise require an
  // explicit enable before binding it to a different identity.
  if (
    !userInitiated
    && preference === null
    && preexistingBrowserSub
    && !knownBackendEndpoint
  ) {
    // Once the current user's backend push state is loaded, this endpoint is
    // known not to belong to them. Invalidate it so a prior user on a shared
    // browser cannot keep receiving private pushes. If the payload is still
    // loading, preserve it and retry later rather than making that assumption.
    if (hasLoadedBackendPushState()) {
      if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
      let browserCleaned = false;
      let cleanupMessage = 'Push unsubscribe was not confirmed.';
      try {
        browserCleaned = (await subscription.unsubscribe()) !== false;
      } catch (err: any) {
        cleanupMessage = err?.message || 'Push unsubscribe failed.';
      }
      if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
      if (!browserCleaned) {
        // Persist an inactive lifecycle so every later startup retries browser
        // cleanup instead of silently accepting a prior user's live endpoint.
        const cleanupLifecycle = transitionLifecycle('inactive', 'sign_out');
        await deactivateBackendPush(auth, {
          deviceId,
          reason: 'sign_out',
          lifecycleGeneration: cleanupLifecycle.generation,
          subscription,
        });
        return {
          success: false,
          message: cleanupMessage,
        };
      }
    }
    return { success: true, skipped: true };
  }

  if (preference === null && knownBackendEndpoint) {
    // One-time migration for users who enabled browser push before the durable
    // per-user preference existed.
    if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
    writePreference(auth.cognito_id, 'enabled');
  }

  // Reassert the browser state through the idempotent backend registration.
  // This repairs stale/inactive server rows even when Redux still has an older
  // user snapshot; it does not send a confirmation unless this call came from
  // the explicit enable action.
  const payload: {
    subscription: any;
    config: PushSubscriptionConfig;
    user_initiated: boolean;
  } = {
    subscription,
    config: {
      device: JSON.stringify(describeDevice()),
      device_id: deviceId,
      // time_zone is JSONB. readTimezone() returns a bare IANA string
      // (e.g. "America/New_York") — Postgres's JSONB parser rejects it
      // because a bare unquoted string isn't valid JSON. Stringifying
      // produces the literal `"America/New_York"` (with quotes) which
      // is. Same fix the `device` column needed in PR #148.
      time_zone: JSON.stringify(readTimezone()),
      lifecycle_generation: lifecycle.generation,
    },
    // The backend only sends the enable-confirmation push for direct user
    // actions. Startup, sign-in recovery, and endpoint rotation are silent.
    user_initiated: userInitiated,
  };

  try {
    if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
    const res = await apiPost(
      `/users/create-push-subscription/${auth.account_id}/${auth.cognito_id}`,
      payload,
    );
    const postedSuperseded = supersededRegistrationResult(
      await stopSupersededRegistration(lifecycle, subscription),
    );
    if (postedSuperseded) return postedSuperseded;
    if (res?.success || (res?.message && /already exists/i.test(res.message))) {
      writePreference(auth.cognito_id, 'enabled');
      const alreadyActive = res?.already_active === true
        || (preexistingBrowserSub && knownBackendEndpoint);
      return alreadyActive ? { success: true, alreadyActive: true } : { success: true };
    }
    if (res?.expired === true) {
      let invalidated = false;
      try {
        invalidated = (await subscription.unsubscribe()) !== false;
      } catch {
        // Preserve opt-in and let a later reconcile retry if the browser will
        // not release a provider-dead endpoint yet.
      }
      if (invalidated && retryExpired) {
        return registerWebPush({
          userInitiated,
          retryExpired: false,
          expectedLifecycle: lifecycle,
        });
      }
      return {
        success: false,
        expired: true,
        message: res?.message || 'Push subscription expired. Please try again.',
      };
    }
    return { success: false, message: res?.message || 'Could not register subscription.' };
  } catch (err: any) {
    if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
    return { success: false, message: err?.message || 'Network error registering subscription.' };
  }
}

/**
 * Explicit user enable. May request permission and create a browser
 * subscription. Concurrent enable/reconcile/unsubscribe operations are
 * serialized so PushManager and backend state cannot race.
 */
export function subscribeWebPush(): Promise<SubscribeResult> {
  // Record the latest intent before waiting behind any in-flight physical
  // mutation. A queued disable/sign-out can then fence this work immediately.
  const lifecycle = transitionLifecycle('active');
  return serializeOperation(() => (
    registerWebPush({ userInitiated: true, expectedLifecycle: lifecycle })
  ));
}

/**
 * Silent startup/rotation maintenance. It may reconcile an existing legacy
 * subscription. It only recreates a missing one when this user has a durable
 * explicit opt-in for this installation; absent/disabled preferences never
 * create or prompt.
 */
export function reconcileWebPush(): Promise<SubscribeResult> {
  const lifecycle = readLifecycle();
  return serializeOperation(() => (
    registerWebPush({ userInitiated: false, expectedLifecycle: lifecycle })
  ));
}

/**
 * Browser-side unsubscribe + flag the backend row as inactive. A settings
 * opt-out records `disabled`; sign-out preserves the user's prior preference
 * so the same user can be restored silently on a later sign-in.
 */
export function unsubscribeWebPush(options: UnsubscribeOptions = {}): Promise<SubscribeResult> {
  const reason = options.reason ?? 'user_disabled';
  const lifecycle = transitionLifecycle('inactive', reason);
  const auth = getAuthIdentity();
  const deviceId = getOrCreateDeviceId();
  if (auth && reason === 'user_disabled') {
    // Persist the latest user intent immediately, even when browser cleanup is
    // queued behind a slow registration.
    writePreference(auth.cognito_id, 'disabled');
  }

  return serializeOperation(async () => {
    let endpoint: string | null = null;
    let subscriptionCapability: PushSubscription | null = null;
    let browserCleaned = false;
    let browserError: string | null = null;
    const supported = hasPushSupport();
    if (supported) {
      const hasRegistration = await hasAnyServiceWorkerRegistration();
      if (!hasRegistration) {
        // No registration means this origin cannot retain a PushManager
        // subscription; backend installation cleanup is sufficient.
        browserCleaned = true;
      } else if (!lifecycleMatches(lifecycle)) {
        return { success: true, skipped: true };
      } else {
        const reg = await awaitActiveServiceWorker();
        if (!reg) {
          browserError = 'Service worker not ready.';
        } else {
          try {
            const subscription = await reg.pushManager.getSubscription();
            subscriptionCapability = subscription;
            if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
            if (subscription) {
              endpoint = subscription.endpoint;
              browserCleaned = (await subscription.unsubscribe()) !== false;
              if (!browserCleaned) browserError = 'Push unsubscribe was not confirmed.';
            } else {
              browserCleaned = true;
            }
          } catch (err: any) {
            browserError = err?.message || 'Push unsubscribe failed.';
          }
        }
      }
    }

    // Always attempt installation-scoped backend cleanup, even when browser
    // support/readiness or unsubscribe failed. Include the exact capability
    // when it was readable so a shared-browser orphan can leave a cross-user
    // tombstone. Cleaning only B's rows is not sufficient while A's origin-
    // level subscription may still be live.
    if (!lifecycleMatches(lifecycle)) return { success: true, skipped: true };
    const backendCleaned = await deactivateBackendPush(auth, {
      endpoint,
      deviceId,
      reason,
      lifecycleGeneration: lifecycle.generation,
      subscription: subscriptionCapability,
    });

    if (supported && !browserCleaned) {
      return { success: false, message: browserError || 'Could not disable browser notifications.' };
    }
    if (!backendCleaned) {
      return !supported
        ? { success: false, unsupported: true }
        : { success: false, message: 'Browser notifications were removed, but server cleanup is still pending.' };
    }
    return { success: true };
  });
}
